// Editing a stored secret's settings (`PUT /api/credentials/{id}`, `UpdateCredential`): the
// form's starting text from the credential, the document from what the user typed, and what
// stops a save. The API replaces `injection` and `worker_exposed` with what it is sent, so the
// document always carries both; the scope is replaced only for a template with a scope facet
// (null keeps the stored one), and facets the form does not show are kept.
import type { Schemas } from "../api/client";
import { messages } from "../i18n";
import { type Credential, DEFAULT_HEADER, listItems, type Template } from "./templates";

export type UpdateCredential = Schemas["UpdateCredential"];

export interface SecretEdit {
  name: string;
  /** The template facet's items as typed (comma- or space-separated). */
  scope: string;
  hosts: string;
  header: string;
  workerExposed: boolean;
}

type Scope = Record<string, string[]>;

function storedScope(credential: Pick<Credential, "scope">): Scope {
  const scope = credential.scope;
  if (!scope || typeof scope !== "object") return {};
  return Object.fromEntries(
    Object.entries(scope as Record<string, unknown>).filter((e): e is [string, string[]] => Array.isArray(e[1])),
  );
}

/** Whether the form asks for allowed hosts and a header: the template's rule, or one already stored. */
export function hasInjection(credential: Pick<Credential, "injection">, template: Template | undefined): boolean {
  return !!credential.injection || !!template?.injection;
}

export function editStart(credential: Credential, template: Template | undefined): SecretEdit {
  const facet = template?.scope?.facet;
  return {
    name: credential.name,
    scope: facet ? (storedScope(credential)[facet] ?? []).join(", ") : "",
    hosts: credential.injection?.hosts.join(", ") ?? "",
    header: credential.injection?.header ?? DEFAULT_HEADER,
    workerExposed: credential.worker_exposed,
  };
}

export function updatePayload(credential: Credential, template: Template | undefined, edit: SecretEdit): UpdateCredential {
  let scope: Scope | null = null;
  const facet = template?.scope?.facet;
  if (facet) {
    const { [facet]: _, ...others } = storedScope(credential);
    const items = listItems(edit.scope);
    scope = items.length > 0 ? { ...others, [facet]: items } : others;
  }
  return {
    name: edit.name.trim(),
    scope,
    injection: hasInjection(credential, template)
      ? { hosts: listItems(edit.hosts).map((h) => h.toLowerCase()), header: edit.header.trim() }
      : null,
    worker_exposed: edit.workerExposed,
  };
}

/** Why the form cannot be saved yet, or null. */
export function editProblem(credential: Credential, template: Template | undefined, edit: SecretEdit): string | null {
  const t = messages().secrets.forms.edit;
  if (edit.name.trim() === "") return t.nameRequired;
  if (hasInjection(credential, template)) {
    if (listItems(edit.hosts).length === 0) return t.hostsRequired;
    if (edit.header.trim() === "") return t.headerRequired;
  }
  return null;
}
